import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertCircleIcon, CameraIcon, CheckCircle2Icon, Loader2Icon } from 'lucide-react';
import { FormField, inputClassName } from './FormField';
import { useEstimateForm } from '../hooks/useEstimateForm';
import { serviceOptions } from '../data/services';
import { shop } from '../data/shop';
import type { ClaimType } from '../types/estimate';
const claimOptions: {
  value: ClaimType;
  label: string;
}[] = [{
  value: 'insurance',
  label: 'Insurance claim'
}, {
  value: 'self-pay',
  label: 'Paying myself'
}, {
  value: 'unsure',
  label: 'Not sure yet'
}];
const ease = [0.23, 1, 0.32, 1] as const;
export function EstimateForm() {
  const {
    values,
    photos,
    errors,
    status,
    updateField,
    updatePhotos,
    submit,
    reset,
    maxPhotos
  } = useEstimateForm();
  const isSubmitting = status === 'submitting';
  const firstName = values.name.trim().split(' ')[0];
  const errorProps = (field: keyof typeof values) => ({
    'aria-invalid': Boolean(errors[field]),
    'aria-describedby': errors[field] ? `estimate-${field}-error` : undefined
  });
  return <div className="rounded-xl bg-surface p-6 text-ink shadow-xl sm:p-10" data-id="element-54" data-mp="m9g3h5dxmuc0">
      <AnimatePresence mode="wait" initial={false} data-id="element-55" data-mp="7m3gdc8h8gji">
        {status === 'success' ? <motion.div key="success" initial={{
        opacity: 0,
        scale: 0.98
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.25,
        ease
      }} className="flex min-h-[480px] flex-col items-start justify-center" role="status" data-id="element-56" data-mp="ckeq7myrur1q">
            <CheckCircle2Icon className="h-10 w-10 text-success" aria-hidden="true" data-id="element-57" data-mp="3ktevi8hczgid" />
            <h3 className="mt-6 font-display text-2xl font-bold tracking-tight" data-id="element-58" data-mp="3ps9d573laz0w">
              Thanks{firstName ? `, ${firstName}` : ''}. We've got your request.
            </h3>
            <p className="mt-3 max-w-md leading-relaxed text-muted" data-id="element-59" data-mp="1o0d361x2lx88">
              An estimator will call you at <span className="font-semibold text-ink" data-id="element-60" data-mp="1mj7hi42t92gl">{values.phone}</span>. Want to
              talk now? Call{' '}
              <a href={shop.phoneHref} className="font-semibold text-ink underline underline-offset-2" data-id="element-61" data-mp="1hijnran767io">
                {shop.phone}
              </a>
              .
            </p>
            <button type="button" onClick={reset} className="mt-8 rounded-md border border-line px-5 py-2.5 text-sm font-semibold transition-colors duration-150 hover:bg-paper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink" data-id="element-62" data-mp="17lx9x5taggq5">
              Send another request
            </button>
          </motion.div> : <motion.form key="form" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease
      }} onSubmit={submit} noValidate aria-labelledby="estimate-form-heading" data-id="element-63" data-mp="1qkt0m75xucz8">
            <h3 id="estimate-form-heading" className="font-display text-xl font-bold tracking-tight" data-id="element-64" data-mp="2rimdwj5wxv58">
              Request an estimate
            </h3>
            <p className="mt-1 text-sm text-muted" data-id="element-65" data-mp="30i96qgkktsmo">A few details and photos help us give you an accurate quote.</p>

            <div className="mt-8 grid gap-5 sm:grid-cols-2" data-id="element-66" data-mp="35h2ju7vybhmb">
              <FormField id="estimate-name" label="Full name" error={errors.name} data-id="element-67" data-mp="35f9d6slc8xeb">
                <input id="estimate-name" type="text" autoComplete="name" value={values.name} onChange={e => updateField('name', e.target.value)} className={inputClassName} {...errorProps('name')} data-id="element-68" data-mp="3afxdk4vgiql7" />
              </FormField>

              <FormField id="estimate-phone" label="Phone" error={errors.phone} data-id="element-69" data-mp="3t7pydc0dh5hu">
                <input id="estimate-phone" type="tel" autoComplete="tel" placeholder="(406) 555-0123" value={values.phone} onChange={e => updateField('phone', e.target.value)} className={inputClassName} {...errorProps('phone')} data-id="element-70" data-mp="203ylugrxn69" />
              </FormField>

              <FormField id="estimate-email" label="Email" optional error={errors.email} data-id="element-71" data-mp="13ih4sd6fvrvi">
                <input id="estimate-email" type="email" autoComplete="email" value={values.email} onChange={e => updateField('email', e.target.value)} className={inputClassName} {...errorProps('email')} data-id="element-72" data-mp="fscz809oxi1v" />
              </FormField>

              <FormField id="estimate-vehicle" label="Vehicle" error={errors.vehicle} data-id="element-73" data-mp="208zrdd94cumu">
                <input id="estimate-vehicle" type="text" placeholder="e.g. 2019 Subaru Outback" value={values.vehicle} onChange={e => updateField('vehicle', e.target.value)} className={inputClassName} {...errorProps('vehicle')} data-id="element-74" data-mp="20auft1jln4vo" />
              </FormField>

              <FormField id="estimate-service" label="What do you need?" optional className="sm:col-span-2" data-id="element-75" data-mp="1tv40jn4leook">
                <select id="estimate-service" value={values.service} onChange={e => updateField('service', e.target.value)} className={inputClassName} data-id="element-76" data-mp="2mhf2b13xqvmy">
                  <option value="" data-id="element-77" data-mp="2cqu2jacizl2g">Select a service</option>
                  {serviceOptions.map(option => <option key={option} value={option} data-id="element-78" data-mp="2hp3wjhmikfmh">
                      {option}
                    </option>)}
                </select>
              </FormField>

              <fieldset className="sm:col-span-2" data-id="element-79" data-mp="3a7rfaqe1fike">
                <legend className="mb-1.5 text-sm font-medium" data-id="element-80" data-mp="3k2zsw5er172y">How will this be paid?</legend>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3" data-id="element-81" data-mp="3f3vgl5wj2t8e">
                  {claimOptions.map(option => {
                const checked = values.claimType === option.value;
                return <label key={option.value} className={`flex cursor-pointer items-center justify-center whitespace-nowrap rounded-md border px-3 py-2.5 text-sm font-medium transition-colors duration-150 focus-within:ring-2 focus-within:ring-ink/20 ${checked ? 'border-ink bg-ink text-white' : 'border-line text-ink hover:border-ink/40'}`} data-id="element-82" data-mp="36402as7oei45">
                        <input type="radio" name="claimType" value={option.value} checked={checked} onChange={() => updateField('claimType', option.value)} className="sr-only" data-id="element-83" data-mp="2idmitq35prl6" />
                        {option.label}
                      </label>;
              })}
                </div>
              </fieldset>

              <FormField id="estimate-description" label="Describe the damage" error={errors.description} className="sm:col-span-2" data-id="element-84" data-mp="16w01zuxp0dgj">
                <textarea id="estimate-description" rows={4} placeholder="Where is the damage and how did it happen?" value={values.description} onChange={e => updateField('description', e.target.value)} className={`${inputClassName} resize-y`} {...errorProps('description')} data-id="element-85" data-mp="f4kfs201ssqu" />
              </FormField>

              <div className="sm:col-span-2" data-id="element-86" data-mp="3rko1vse9esm8">
                <label htmlFor="estimate-photos" className="flex cursor-pointer items-center gap-4 rounded-md border border-dashed border-line px-4 py-4 transition-colors duration-150 hover:border-ink/40 focus-within:ring-2 focus-within:ring-ink/20" data-id="element-87" data-mp="2kyn3zvmvb9pr">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-paper" data-id="element-88" data-mp="2py00l83jjv5j">
                    <CameraIcon className="h-5 w-5 text-muted" aria-hidden="true" data-id="element-89" data-mp="33wzdxjllsnme" />
                  </span>
                  <span className="flex flex-col" data-id="element-90" data-mp="2yy6a6iw4cazf">
                    <span className="text-sm font-medium" data-id="element-91" data-mp="3dnub3j0e17mm">
                      {photos.length > 0 ? `${photos.length} photo${photos.length === 1 ? '' : 's'} selected` : 'Add photos of the damage'}
                    </span>
                    <span className="text-xs text-muted" data-id="element-92" data-mp="3mmfrp8mauckr">Optional · up to {maxPhotos} images</span>
                  </span>
                  <input id="estimate-photos" type="file" accept="image/*" multiple onChange={e => updatePhotos(e.target.files)} className="sr-only" data-id="element-93" data-mp="55tmtb62t0ce" />
                </label>
              </div>
            </div>

            <AnimatePresence data-id="element-94" data-mp="12wxz2py0800">
              {status === 'error' && <motion.div role="alert" initial={{
            opacity: 0,
            y: -4
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.2,
            ease
          }} className="mt-6 flex items-start gap-3 rounded-md border border-danger/30 bg-danger/5 px-4 py-3 text-sm text-ink" data-id="element-95" data-mp="3ik7xp8sza19z">
                  <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-danger" aria-hidden="true" data-id="element-96" data-mp="2zlltvm62tw2d" />
                  <span data-id="element-97" data-mp="34l89215gtmdt">
                    We couldn't send your request. Please try again, or call us at{' '}
                    <a href={shop.phoneHref} className="font-semibold underline underline-offset-2" data-id="element-98" data-mp="17lkb4jeu6i6f">
                      {shop.phone}
                    </a>
                    .
                  </span>
                </motion.div>}
            </AnimatePresence>

            <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-99" data-mp="12mh4iuj7k3wc">
              <p className="text-sm text-muted" data-id="element-100" data-mp="1qcl9b584u9ox">Free, with no obligation.</p>
              <button type="submit" disabled={isSubmitting} className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent px-6 py-3 text-base font-semibold text-white transition-colors duration-150 hover:bg-accent-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70" data-id="element-101" data-mp="1vboc0j0n43tq">
                {isSubmitting && <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-102" data-mp="2nglmd0h7dd4" />}
                {isSubmitting ? 'Sending…' : 'Request my estimate'}
              </button>
            </div>
          </motion.form>}
      </AnimatePresence>
    </div>;
}